import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ROUTES } from '@/routes/route.config';
import { publicAuthApi } from '@/services/publicAuth.service';
import { acceptInviteFormSchema } from '@/validators/acceptInvite.validator';

interface FormValues {
  password: string;
  confirm: string;
}

type FieldErrors = Partial<Record<keyof FormValues, string>>;

export interface UseAcceptInvitePageResult {
  form: FormValues;
  errors: FieldErrors;
  hasToken: boolean;
  isSubmitting: boolean;
  submitError: string | null;
  isDone: boolean;
  onChange: (field: keyof FormValues, value: string) => void;
  onSubmit: () => void;
  onGoToLogin: () => void;
}

export const useAcceptInvitePage = (): UseAcceptInvitePageResult => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t') ?? '';

  const [form, setForm] = useState<FormValues>({ password: '', confirm: '' });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setSubmitting] = useState(false);
  const [isDone, setDone] = useState(false);

  const onSubmit = (): void => {
    setSubmitError(null);

    const parsed = acceptInviteFormSchema.safeParse(form);
    if (!parsed.success) {
      const next: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof FormValues;
        if (key && !next[key]) next[key] = issue.message;
      }
      setErrors(next);
      return;
    }
    setErrors({});
    setSubmitting(true);

    publicAuthApi
      .acceptInvite(token, parsed.data.password)
      .then(() => setDone(true))
      .catch((error: unknown) => {
        setSubmitError(
          error instanceof Error ? error.message : 'Could not set your password. Try again.',
        );
      })
      .finally(() => setSubmitting(false));
  };

  return {
    form,
    errors,
    hasToken: token.length > 0,
    isSubmitting,
    submitError,
    isDone,
    onChange: (field, value) => {
      setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
      setSubmitError(null);
      setForm((prev) => ({ ...prev, [field]: value }));
    },
    onSubmit,
    onGoToLogin: () => navigate(ROUTES.login, { replace: true }),
  };
};
